import { useRef, useCallback, useState, useEffect } from 'react';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { zIndices } from '@hivebrite/design-system/Theme';
import { useOnClickOutside } from '@hivebrite/react-hooks';

import { useBuilderContext, useComponentContext } from 'hb-builder/hooks';
import { getClosestValue } from 'hb-builder/utils/getClosestValue';

const Resize = ({
  acceptedValues,
  dataTestId,
  initialValue,
  isDragging,
  onChange,
  positionsHandler,
  style,
  type,
}) => {
  const { fullDocument, isResizing, setIsResizing, setResizedItemId } =
    useBuilderContext();
  const { item, type: componentType } = useComponentContext();

  const handleInitialRef = useRef();
  const handlersRef = useRef({});
  const initialValueRef = useRef();
  const positionResizingRef = useRef();
  const resizeRef = useRef();

  const [value, setValue] = useState(initialValue);
  const [tmpValue, setTmpValue] = useState();
  const [diff, setDiff] = useState();

  const handleIsResizing = useCallback(
    (isResizing) => {
      setIsResizing?.(isResizing ? type : null);
    },
    [type],
  );

  const handleMouseDown = useCallback(
    (positionHandler) => (e) => {
      e.preventDefault();
      e.stopPropagation();

      setResizedItemId(item.id);

      if (resizeRef.current) {
        const { height, width } = resizeRef.current.getBoundingClientRect();

        const { y, x } =
          handlersRef.current[positionHandler].getBoundingClientRect();

        initialValueRef.current = parseInt(
          tmpValue > 0 ? tmpValue : type === 'y' ? height : width,
          10,
        );
        handleInitialRef.current = type === 'y' ? y : x;
        positionResizingRef.current = positionHandler;

        handleIsResizing(positionHandler);

        attachDocumentEvents();
      }
    },
    [value, type],
  );

  const handleMouseMove = useCallback(
    (e) => {
      let value;

      if (type === 'y') {
        const difference = e.clientY - handleInitialRef.current;

        value = parseInt(initialValueRef.current + difference, 10);
      } else {
        const difference = e.clientX - handleInitialRef.current;

        setDiff(difference);
        value = parseInt(
          initialValueRef.current -
            (positionResizingRef.current === 'left' ? difference : -difference),
          10,
        );
      }

      setTmpValue(value);
    },
    [type],
  );

  const handleMouseUp = useCallback(
    (e) => {
      e.stopPropagation();
      let finalValue;

      if (type === 'y') {
        // resize section
        const rawDiff = e.clientY - handleInitialRef.current;

        finalValue = rawDiff;
      } else {
        // resize column
        const difference = e.clientX - handleInitialRef.current;

        const newValue = parseInt(
          initialValueRef.current -
            (positionResizingRef.current === 'left' ? difference : -difference),
          10,
        );

        // when type === "x", value is %
        finalValue = (newValue * value) / initialValueRef.current;
      }

      setTmpValue(null);

      if (acceptedValues) {
        const closest = getClosestValue(acceptedValues, finalValue);

        setValue(closest);
      } else {
        setValue(finalValue);
      }

      handleIsResizing(false);

      detachDocumentsEvents();
    },
    [value],
  );

  const attachDocumentEvents = () => {
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);
  };

  const detachDocumentsEvents = () => {
    document.removeEventListener('mouseup', handleMouseUp);
    document.removeEventListener('mousemove', handleMouseMove);
  };

  const handleClickOutside = () => setResizedItemId(null);

  useOnClickOutside(resizeRef, handleClickOutside);

  useEffect(() => {
    if (initialValue !== value) {
      onChange({
        acceptedValues,
        value,
        position: positionResizingRef.current,
      });
    }
  }, [value]);

  useEffect(() => {
    if (initialValue !== value) {
      setValue(initialValue);
    }
  }, [initialValue, fullDocument]);

  return (
    !isDragging && (
      <AbsoluteWrapper
        data-testid={dataTestId}
        ref={resizeRef}
        $componentType={componentType}
        $type={type}
        style={{
          ...style,
          ...(type === 'x'
            ? {
                transform:
                  positionResizingRef.current === 'left' &&
                  tmpValue &&
                  `translate3D(${diff}px, 0, 0)`,
                width: tmpValue && `${tmpValue}px`,
                height: '100% !important',
              }
            : {
                height: tmpValue && `${Math.ceil(tmpValue)}px`,
              }),
        }}
      >
        {positionsHandler &&
          positionsHandler.map((positionHandler) => (
            <PositionWrapper key={positionHandler} $type={type}>
              <HandlerWrapper
                onMouseDown={handleMouseDown(positionHandler)}
                ref={(node) => (handlersRef.current[positionHandler] = node)}
                $handlerPadding={style.padding}
                $handlerPosition={positionHandler}
                $type={type}
              >
                <Handler $componentType={componentType} $type={type} />
              </HandlerWrapper>
              {type === 'y' && isResizing && (
                <HorizontalIndicator $componentType={componentType} />
              )}
            </PositionWrapper>
          ))}

        {isResizing && <Layer />}
      </AbsoluteWrapper>
    )
  );
};

export default Resize;

Resize.defaultProps = {};

const AbsoluteWrapper = styled(Box)`
  ${({ theme, $type }) =>
    $type === 'x'
      ? css`
          border: 1px solid ${theme.colors.primary};
          outline: 1px solid ${theme.colors.primary};
          border-radius: 2px;
        `
      : ''}
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  pointer-events: none;
  position: absolute;
`;

const Handler = styled(Box)`
  ${({ $type }) => css`
    position: absolute;
    ${$type === 'x'
      ? css`
          cursor: col-resize;
          top: 0;
          bottom: 0;
          width: 10px;
        `
      : css`
          cursor: row-resize;
          height: 10px;
          width: 100%;
        `}
  `}
`;

const HandlerWrapper = styled(Box)`
  pointer-events: all;
  position: absolute;
  ${({ $type, $handlerPosition }) => {
    const newStyleWithFlag =
      $type === 'x'
        ? css`
            height: 100%;
            ${$handlerPosition}: -1px;
            width: 10px;
          `
        : css`
            bottom: 0px;
            height: 10px;
            width: 100%;
          `;

    return newStyleWithFlag;
  }}
`;

const HorizontalIndicator = styled.div`
  height: 2px;
  width: 100vw;
  z-index: ${zIndices.positioned};
  background-color: ${({ theme }) => theme.colors.primary};
  bottom: 0;
  left: 0;
  position: absolute;
`;

const Layer = styled.div`
  pointer-events: all;
  bottom: 0;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 400;
`;

const PositionWrapper = styled(Box).attrs(() => ({
  width: '100%',
  height: '100%',
}))`
  position: absolute;
  z-index: 400;
`;
